import { useCallback, useState, useTransition } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import translate from '@i18n'
import { getFmtCompetitionEntityIdsSorted } from '@hooks/useFmtCompetitionEntityIdsSorted'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import PrimaryCTAButton from '@components/Buttons/PrimaryCTAButton'
import FmTOnboardingContainer from '@components/FmtOnboardingContainer/FmTOnboardingContainer'
import FmtTeamFilter from '@components/FmtTeamFilter'

import FollowMyTeamDrawerHeader from '../FollowMyTeamDrawer/Header'
import FollowMyTeamSportCompetitorsList from '../FollowMyTeamSelectTeam/FollowMyTeamSportCompetitorsList'
import Searchbar from '../Searchbar'
import SearchbarContainer from '../Searchbar/SearchbarContainer'
import FollowMyTeamMyTeamsList from './FollowMyTeamMyTeamsList'

type SelectTeamsWrapperProps = {
  showSportCompetitorsList?: boolean
  showFmtOnboardingContainer?: boolean
  showFilters?: boolean
}

const Container = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    > * {
      padding-left: ${spacing16};
      padding-right: ${spacing16};
    }
  `}
`

const SelectTeamWrapper = styled.div<SelectTeamsWrapperProps>`
  ${({
    theme: {
      spacing: { spacing32 },
    },
    showFmtOnboardingContainer,
    showFilters,
  }) => css`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: ${spacing32};
    max-height: calc(
      100dvh -
        ${showFmtOnboardingContainer && showFilters
          ? '330px'
          : showFmtOnboardingContainer
            ? '253px'
            : showFilters
              ? '184px'
              : '120px'}
    );
    max-height: calc(
      100vh -
        ${showFmtOnboardingContainer && showFilters
          ? '330px'
          : showFmtOnboardingContainer
            ? '253px'
            : showFilters
              ? '184px'
              : '120px'}
    );

    ${desktopCSS(css`
      max-height: calc(
        100dvh -
          ${showFmtOnboardingContainer && showFilters
            ? '380px'
            : showFmtOnboardingContainer
              ? '313px'
              : showFilters
                ? '184px'
                : '120px'}
      );
      max-height: calc(
        100vh -
          ${showFmtOnboardingContainer && showFilters
            ? '380px'
            : showFmtOnboardingContainer
              ? '313px'
              : showFilters
                ? '184px'
                : '120px'}
      );
    `)};
  `}
`

const MyTeamsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16, spacing32 },
    },
  }) => css`
    padding-top: ${spacing16};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    max-height: calc(100vh - 48px - ${spacing32});
    max-height: calc(100dvh - 48px - ${spacing32});
  `}
`

export const StyledStickyContainerWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16 },
    },
  }) => css`
    display: flex;
    flex-direction: column;
    gap: ${spacing16};
    padding: ${spacing16};
    overflow: visible;
  `}
`

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  width: 100%;
`

const FollowMyTeamMyTeams = ({ onClose }: { onClose: () => void }) => {
  const [showFmtOnboardingContainer, setShowFmtOnboardingContainer] =
    useState<boolean>(true)
  const [showSportCompetitorsList, setShowSportCompetitorsList] =
    useState(false)
  const [selectedFilterId, setSelectedFilterId] = useState<string>('')

  const [currentSearchTerm, setCurrentSearchTerm] = useState('')
  const [, startTransition] = useTransition()

  const handleBack = useCallback(() => {
    setShowSportCompetitorsList(false)
  }, [])

  const handleAddTeam = useCallback(() => {
    setShowSportCompetitorsList(true)
  }, [])

  const onSearchTermUpdate = useCallback((searchTerm: string) => {
    setSelectedFilterId('')
    startTransition(() => {
      setCurrentSearchTerm(searchTerm)
    })
  }, [])

  const onMyTeamsImpressionEvent = useCallback<TrackingFnType>(
    () => ({
      event: 'my_teams_impression',
    }),
    [],
  )

  const onSelectTeamImpressionEvent = useCallback<TrackingFnType>(
    ({ queryClient }) => ({
      event: 'select_team_impression',
      element: getFmtCompetitionEntityIdsSorted(queryClient)?.length ?? 0,
    }),
    [],
  )

  const trackedOnMyTeamsImpression = useTracking(onMyTeamsImpressionEvent)
  const trackedOnSelectTeamImpression = useTracking(onSelectTeamImpressionEvent)

  const myTeamsViewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnMyTeamsImpression,
  })

  const selectTeamViewportRef = useViewportTracking({
    track: true,
    onImpression: trackedOnSelectTeamImpression,
  })

  return (
    <Container>
      {showSportCompetitorsList ? (
        <StyledStickyContainerWrapper>
          <SearchbarContainer onClose={onClose} onBack={handleBack}>
            <Searchbar onSearchTermUpdate={onSearchTermUpdate} />
          </SearchbarContainer>
          <FmTOnboardingContainer
            position="selectTeam"
            setShowFmtOnboardingContainer={setShowFmtOnboardingContainer}
            showFmtOnboardingContainer={showFmtOnboardingContainer}
          />
          <FmtTeamFilter
            setSelectedFilterId={setSelectedFilterId}
            selectedFilterId={selectedFilterId}
            currentSearchTerm={currentSearchTerm}
          />
        </StyledStickyContainerWrapper>
      ) : (
        <FollowMyTeamDrawerHeader
          title={translate('followMyTeam.myTeams')}
          showBackButton={showSportCompetitorsList}
          onBack={handleBack}
          onClose={onClose}
        />
      )}
      {showSportCompetitorsList ? (
        <SelectTeamWrapper
          ref={selectTeamViewportRef}
          showFmtOnboardingContainer={showFmtOnboardingContainer}
          showFilters={!currentSearchTerm}
        >
          <FollowMyTeamSportCompetitorsList
            currentSearchTerm={currentSearchTerm}
            currentFilterId={selectedFilterId}
          />
        </SelectTeamWrapper>
      ) : (
        <MyTeamsWrapper ref={myTeamsViewportRef}>
          <FmTOnboardingContainer
            position="myTeams"
            setShowFmtOnboardingContainer={setShowFmtOnboardingContainer}
            showFmtOnboardingContainer={showFmtOnboardingContainer}
          />
          <StyledPrimaryCTAButton
            onClick={handleAddTeam}
            colorVariant="black"
            size="large"
            iconName="plus-circle"
            iconLeft={true}
          >
            {translate('followMyTeamOverview.addTeam')}
          </StyledPrimaryCTAButton>
          <FollowMyTeamMyTeamsList />
        </MyTeamsWrapper>
      )}
    </Container>
  )
}

export default FollowMyTeamMyTeams
